import "@dxos/node-std/globals"

// packages/core/mesh/teleport-extension-replicator/src/replicator-extension.ts
import assert from "@dxos/node-std/assert";
import { asyncTimeout, DeferredTask, synchronized } from "@dxos/async";
import { Context } from "@dxos/context";
import { failUndefined } from "@dxos/debug";
import { PublicKey } from "@dxos/keys";
import { log, logInfo } from "@dxos/log";
import { schema } from "@dxos/protocols";
import { createProtoRpcPeer, RpcClosedError } from "@dxos/rpc";
import { ComplexMap } from "@dxos/util";
var __decorate = function(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function")
    r = Reflect.decorate(decorators, target, key, desc);
  else
    for (var i = decorators.length - 1; i >= 0; i--)
      if (d = decorators[i])
        r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var ReplicatorExtension = class {
  constructor() {
    this._ctx = new Context({
      onError: (err) => {
        var _a;
        log.catch(err, {}, {
          file: "replicator-extension.ts",
          line: 31,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        (_a = this._extensionContext) == null ? void 0 : _a.close(err);
      }
    });
    this._feeds = new ComplexMap(PublicKey.hash);
    this._streams = new ComplexMap(PublicKey.hash);
    this._options = {
      upload: false
    };
    this._updateTask = new DeferredTask(this._ctx, async () => {
      try {
        if (this._extensionContext.initiator === false) {
          await this._rpc.rpc.ReplicatorService.updateFeeds({
            feeds: Array.from(this._feeds.values()).map((feed) => ({
              feedKey: feed.key,
              download: true,
              upload: this._options.upload
            }))
          });
        } else if (this._extensionContext.initiator === true) {
          await this._reevaluateFeeds();
        }
      } catch (err) {
        if (err instanceof RpcClosedError) {
          return;
        }
        throw err;
      }
    });
  }
  get extensionInfo() {
    var _a, _b, _c;
    return {
      initiator: (_a = this._extensionContext) == null ? void 0 : _a.initiator,
      localPeerId: (_b = this._extensionContext) == null ? void 0 : _b.localPeerId,
      remotePeerId: (_c = this._extensionContext) == null ? void 0 : _c.remotePeerId,
      feeds: Array.from(this._feeds.keys())
    };
  }
  setOptions(options) {
    this._options = options;
    log("setOptions", {
      options
    }, {
      file: "replicator-extension.ts",
      line: 79,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    if (this._extensionContext) {
      this._updateTask.schedule();
    }
    return this;
  }
  addFeed(feed) {
    this._feeds.set(feed.key, feed);
    log("addFeed", {
      feedKey: feed.key
    }, {
      file: "replicator-extension.ts",
      line: 88,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    if (this._extensionContext) {
      this._updateTask.schedule();
    }
  }
  async onOpen(context) {
    this._extensionContext = context;
    log("open", {}, {
      file: "replicator-extension.ts",
      line: 96,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    this._rpc = createProtoRpcPeer({
      requested: {
        ReplicatorService: schema.getService("dxos.mesh.teleport.replicator.ReplicatorService")
      },
      exposed: {
        ReplicatorService: schema.getService("dxos.mesh.teleport.replicator.ReplicatorService")
      },
      handlers: {
        ReplicatorService: {
          updateFeeds: async ({ feeds }) => {
            log("received feed info", {
              feeds
            }, {
              file: "replicator-extension.ts",
              line: 108,
              scope: this,
              callSite: (f, a) => f(...a)
            });
            assert(this._extensionContext.initiator === true, "Invalid call");
            this._updateTask.schedule();
          },
          startReplication: async ({ info }) => {
            log("starting replication...", {
              info
            }, {
              file: "replicator-extension.ts",
              line: 113,
              scope: this,
              callSite: (f, a) => f(...a)
            });
            assert(this._extensionContext.initiator === false, "Invalid call");
            const streamTag = await this._acceptReplication(info);
            return {
              streamTag
            };
          },
          stopReplication: async ({ info }) => {
            log("stopping replication...", {
              info
            }, {
              file: "replicator-extension.ts",
              line: 122,
              scope: this,
              callSite: (f, a) => f(...a)
            });
            assert(this._extensionContext.initiator === false, "Invalid call");
            await this._stopReplication(info.feedKey);
          }
        }
      },
      port: context.createPort("rpc", {
        contentType: 'application/x-protobuf; messageType="dxos.rpc.Message"'
      })
    });
    await this._rpc.open();
    this._updateTask.schedule();
  }
  async onClose(err) {
    var _a;
    log("close", {
      err
    }, {
      file: "replicator-extension.ts",
      line: 140,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    await this._ctx.dispose();
    await ((_a = this._rpc) == null ? void 0 : _a.close());
    for (const feedKey of this._streams.keys()) {
      await this._stopReplication(feedKey);
    }
  }
  async _reevaluateFeeds() {
    var _a;
    log("_reevaluateFeeds", {}, {
      file: "replicator-extension.ts",
      line: 150,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    for (const feedKey of this._feeds.keys()) {
      if (this._ctx.disposed) {
        return;
      }
      if (this._streams.has(feedKey) && this._options.upload !== ((_a = this._streams.get(feedKey)) == null ? void 0 : _a.info.upload)) {
        try {
          await asyncTimeout(this._stopReplication(feedKey), 1e3);
        } catch (err) {
          log.catch(err, {}, {
            file: "replicator-extension.ts",
            line: 159,
            scope: this,
            callSite: (f, a) => f(...a)
          });
        }
      }
      if (this._ctx.disposed) {
        return;
      }
      if (!this._streams.has(feedKey)) {
        await this._initiateReplication({
          feedKey,
          download: true,
          upload: this._options.upload
        });
      }
    }
  }
  /**
  * Try to initiate feed replication.
  */
  async _initiateReplication(feedInfo) {
    log("initiating replication", {
      feedInfo
    }, {
      file: "replicator-extension.ts",
      line: 180,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    assert(this._extensionContext.initiator === true, "Invalid call");
    assert(!this._streams.has(feedInfo.feedKey), `Replication already in progress for feed: ${feedInfo.feedKey}`);
    const { streamTag } = await this._rpc.rpc.ReplicatorService.startReplication({
      info: feedInfo
    });
    if (!streamTag) {
      return;
    }
    this._replicateFeed(feedInfo, streamTag);
  }
  /**
  * Respond to a remote request to replicate a feed.
  * @returns A stream tag for the replication stream or `undefined` if we don't want to replicate.
  */
  async _acceptReplication(feedInfo) {
    assert(this._extensionContext.initiator === false, "Invalid call");
    if (!this._feeds.has(feedInfo.feedKey) || this._streams.has(feedInfo.feedKey)) {
      return void 0;
    }
    const tag = `feed-${feedInfo.feedKey.toHex()}-${PublicKey.random().toHex().slice(0, 8)}`;
    this._replicateFeed(feedInfo, tag);
    return tag;
  }
  _replicateFeed(info, streamTag) {
    var _a;
    log("replicate", {
      info,
      streamTag
    }, {
      file: "replicator-extension.ts",
      line: 209,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    assert(!this._streams.has(info.feedKey), `Replication already in progress for feed: ${info.feedKey}`);
    const feed = (_a = this._feeds.get(info.feedKey)) != null ? _a : failUndefined();
    const networkStream = this._extensionContext.createStream(streamTag, {
      contentType: "application/x-hypercore"
    });
    const replicationStream = feed.replicate(true, {
      live: true,
      upload: info.upload,
      download: info.download,
      noise: false,
      encrypted: false
    });
    replicationStream.on("error", (err) => {
      if ((err == null ? void 0 : err.message) === "Writable stream closed prematurely" || (err == null ? void 0 : err.message) === "Cannot call write after a stream was destroyed") {
        return;
      }
      log.warn("replication stream error", {
        err,
        info
      }, {
        file: "replicator-extension.ts",
        line: 240,
        scope: this,
        callSite: (f, a) => f(...a)
      });
    });
    this._streams.set(info.feedKey, {
      streamTag,
      networkStream,
      replicationStream,
      info
    });
    networkStream.pipe(replicationStream).pipe(networkStream);
  }
  async _stopReplication(feedKey) {
    const stream = this._streams.get(feedKey);
    if (!stream) {
      return;
    }
    stream.networkStream.destroy();
    this._streams.delete(feedKey);
  }
};
__decorate([
  logInfo
], ReplicatorExtension.prototype, "extensionInfo", null);
__decorate([
  synchronized
], ReplicatorExtension.prototype, "_reevaluateFeeds", null);
__decorate([
  synchronized
], ReplicatorExtension.prototype, "_acceptReplication", null);
export {
  ReplicatorExtension
};
//# sourceMappingURL=index.mjs.map
